// The server response envelope — the contract-locked truth the UI trusts (backend response-envelope.ts +
// verify:seed-contract-parity). data_class + allowed_actions + disabled_reasons are ALWAYS server-derived;
// the UI never invents them. Mirrors the legacy src/shared/services/api-client/envelope.ts reader.

export type DataClass = 'live' | 'starter' | 'template' | 'redacted' | 'public_safe';

/** An envelope-wrapped payload. The server may attach these to any entity/list. */
export interface Enveloped<T> {
  data: T;
  data_class?: DataClass;
  allowed_actions?: string[];
  disabled_reasons?: Record<string, string>;
}

/** Read a possibly-enveloped body: unwrap `{data, data_class, ...}` or pass a bare payload through. */
export function readEnvelope<T>(body: unknown): Enveloped<T> {
  if (body && typeof body === 'object' && 'data' in (body as Record<string, unknown>)) {
    const b = body as Record<string, unknown>;
    return {
      data: b.data as T,
      data_class: b.data_class as DataClass | undefined,
      allowed_actions: Array.isArray(b.allowed_actions) ? (b.allowed_actions as string[]) : undefined,
      disabled_reasons:
        b.disabled_reasons && typeof b.disabled_reasons === 'object'
          ? (b.disabled_reasons as Record<string, string>)
          : undefined,
    };
  }
  return { data: body as T };
}

/** Standard API error envelope (backend middleware/error.ts): { error, code, request_id }. */
export interface ApiErrorBody {
  error: string;
  code: string;
  request_id?: string;
}

export class ApiError extends Error {
  code: string;
  status: number;
  requestId?: string;
  constructor(status: number, body: Partial<ApiErrorBody> & { error: string }) {
    super(body.error);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code || 'INTERNAL_ERROR';
    this.requestId = body.request_id;
  }
}
